import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, Users, TrendUp, Star, ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const PartnerHero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
              <TrendUp className="w-5 h-5 text-blue-400" weight="thin" />
              <span className="text-sm font-medium">Affiliate Systeem</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              Verdien door klanten{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                aan te brengen
              </span>
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed">
              Breng gewoon klanten aan en verdien direct na oplevering. 
              Geen aanmelding, geen verplichtingen, gewoon verdienen.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Start met Verdienen
                <ArrowRight className="ml-2 w-5 h-5" weight="thin" />
              </Link>
              <Link
                to="#how-it-works"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
              >
                Hoe het Werkt
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">€500+</div>
                <div className="text-sm text-gray-300">Gemiddelde commissie</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">25%</div>
                <div className="text-sm text-gray-300">Eerste jaar</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">24/7</div>
                <div className="text-sm text-gray-300">Partner support</div>
              </div>
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <TrendUp className="w-6 h-6 text-white" weight="thin" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Hoe het Verdienmodel Werkt</h3>
                    <p className="text-sm text-gray-300">Simpel en effectief</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Star className="w-5 h-5 text-blue-400" weight="thin" />
                    <span className="text-sm">Per klant: €200 - €1,000 commissie</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <TrendUp className="w-5 h-5 text-indigo-400" weight="thin" />
                    <span className="text-sm">Hoe beter de klant, hoe hoger de beloning</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Handshake className="w-5 h-5 text-blue-500" weight="thin" />
                    <span className="text-sm">Direct uitbetaald na oplevering</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500/20 to-indigo-600/20 rounded-lg p-4 border border-blue-500/30">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">€200 - €1,000</div>
                    <div className="text-sm text-gray-300">Commissie per klant</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full opacity-20 animate-pulse" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full opacity-20 animate-pulse delay-1000" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PartnerHero;
